import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  Textarea,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import DatePickerInput from "components/CustomDateInput/CustomDateInput";
import { checkForbidden } from "helpers/checkForbidden";
import { useBookingActions } from "hooks/useBookingActions";
import { useNotification } from "hooks/useNotification";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { BsSearch } from "react-icons/bs";
import { MdEmail } from "react-icons/md";
import { RiFileExcel2Fill } from "react-icons/ri";
import { RxReset } from "react-icons/rx";
import { useRecoilValue } from "recoil";
import {
  listChannelAtom,
  listShowAtom,
  listUnitAtom,
  paymentMethodListAtom,
  paymentStatusListAtom,
  showTimeListAtom,
  venueListAtom,
  zoneListAtom,
} from "../../state/show";
import "./table.scss";
import { getFirstAndLastDateOfYear } from "helpers/date";
import dayjs from "dayjs";

const sendingStatusOptions = [
  { id: 0, name: "All" },
  { id: 1, name: "Sent" },
  { id: 2, name: "Unsent" },
];

const receivingStatusOptions = [
  { id: "", name: "All" },
  { id: "Pending", name: "Pending" },
  { id: "Unread", name: "Unread" },
  { id: "Read", name: "Read" },
];

const languageOptions = [
  { id: "", name: "All" },
  { id: "en", name: "en" },
  { id: "vi", name: "vi" },
];

function DataTableShowCancelledNotification() {
  const listShow = useRecoilValue(listShowAtom);
  const venueList = useRecoilValue(venueListAtom);
  const listChannel = useRecoilValue(listChannelAtom);
  const listUnit = useRecoilValue(listUnitAtom);
  const listZone = useRecoilValue(zoneListAtom);
  const listPaymentMethod = useRecoilValue(paymentMethodListAtom);
  const listPaymentStatus = useRecoilValue(paymentStatusListAtom);
  const showTimeList = useRecoilValue(showTimeListAtom);

  const bookingStatus = [
    { Id: 0, Name: "All" },
    { Id: 1, Name: "Success" },
    { Id: 2, Name: "Cancel" },
  ];

  const toast = useToast();
  const textColor = useColorModeValue("gray.700", "white");
  const bookingActions = useBookingActions();
  const notificationActions = useNotification();
  const [isFetching, setIsFetching] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [isExportExcel, setIsExportExcel] = useState(false);
  const [highlightDate, setHighlightDate] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isFilterDirty, setIsFilterDirty] = useState(false);
  const [reportTotal, setReportTotal] = useState(0);
  const contentRef = useRef(null);
  const [selectedBookingIds, setSelectedBookingIds] = useState([]);
  const [selectedGuestEmails, setSelectedGuestEmails] = useState([]);
  const { isOpen: isEmailModalOpen, onOpen: onEmailModalOpen, onClose: onEmailModalClose } = useDisclosure();
  const [emailLanguage, setEmailLanguage] = useState("en");
  const [emailCC, setEmailCC] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [templateSubject, setTemplateSubject] = useState("");
  const [templateBody, setTemplateBody] = useState("");
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const {
    register: registerSearch,
    handleSubmit: handleSubmitSearch,
    control: controlSearch,
    watch: watchSearch,
    reset: resetSearch,
    setValue: setValueSearch,
  } = useForm();

  const showSearch = watchSearch("showSearch");
  const venueSearch = watchSearch("venueSearch");
  const channelSearch = watchSearch("channelSearch");
  const allSearchValues = watchSearch();

  useEffect(() => {
    if (hasSearched) {
      setIsFilterDirty(true);
    }
  }, [JSON.stringify(allSearchValues)]);

  useEffect(() => {
    if (channelSearch) {
      bookingActions.getListUnits({ channel_id: channelSearch }, 0, "");
    }
  }, [channelSearch]);

  useEffect(() => {
    if (!contentRef.current) return;
    const container = contentRef.current;

    const handleClick = (e) => {
      const target = e.target;
      if (!target || target.type !== 'checkbox') return;

      // Use setTimeout to ensure checked state is updated
      setTimeout(() => {
        const checkboxes = container.querySelectorAll('.booking-checkbox');
        const selectAllCheckbox = container.querySelector('.select-all-checkbox');

        if (target.classList.contains('select-all-checkbox')) {
          checkboxes.forEach((cb) => {
            cb.checked = target.checked;
          });
        }

        const ids = [];
        const emails = [];
        checkboxes.forEach((cb) => {
          if (cb.checked) {
            const bookingId = cb.getAttribute('data-booking-id');
            const guestEmail = cb.getAttribute('data-guest-email');
            if (bookingId) ids.push(bookingId);
            if (guestEmail) emails.push(guestEmail);
          }
        });

        if (selectAllCheckbox && !target.classList.contains('select-all-checkbox')) {
          selectAllCheckbox.checked = checkboxes.length > 0 && ids.length === checkboxes.length;
        }

        setSelectedBookingIds(ids);
        setSelectedGuestEmails(emails);
        console.log('Selected booking IDs:', ids);
      }, 0);
    };

    container.addEventListener('click', handleClick);

    return () => {
      container.removeEventListener('click', handleClick);
    };
  }, [reportData]);

  useEffect(() => {
    bookingActions.getListShow({ status: 1 }, 0, "");
    bookingActions.getListChannel({ status: 1 }, 0, "");
    bookingActions.getListVenue({ status: 1 }, 0, "");
    bookingActions.getListZones({ status: 1 }, 0, "");
    bookingActions.getListPaymentStatus({ status: 1 }, 0, "");
    bookingActions.getListPaymentMethod({ status: 1 }, 0, "");
    bookingActions.getListShowTime({ status: 1 }, 0, "");
    setValueSearch("show_date_from", new Date());
    setValueSearch("show_date_to", new Date());
  }, []);

  const handleCalendarOpen = async (year, month, isFrom = false) => {
    const dateToCheck = isFrom ? watchSearch("show_date_from") : watchSearch("show_date_to");
    const fallbackDate = new Date();
    const targetYear = year || (dateToCheck ? dateToCheck.getFullYear() : fallbackDate.getFullYear());
    const targetMonth = month ?? (dateToCheck ? dateToCheck.getMonth() : fallbackDate.getMonth());
    const { firstDate, lastDate } = getFirstAndLastDateOfYear(targetYear, targetMonth);
    try {
      const res = await bookingActions.getShowsSchedule({
        show_id: showSearch,
        venue_id: venueSearch,
        from_date: firstDate,
        to_date: lastDate,
      });
      if (res) {
        const dateHighlight = res.map((item) => new Date(item.showdate));
        setHighlightDate(dateHighlight);
      }
    } catch (error) {
      console.error("Failed to fetch show schedule", error);
    }
  };

  async function onSubmitSearch({
    showSearch,
    venueSearch,
    statusSearch,
    channelSearch,
    unitSearch,
    paymentMethodSearch,
    paymentStatusSearch,
    showTimeSearch,
    zoneSearch,
    sendingStatusSearch,
    receivingStatusSearch,
    langSearch,
    searchValue,
  }) {
    const show_date_from = watchSearch("show_date_from");
    const show_date_to = watchSearch("show_date_to");

    if (!show_date_from || !show_date_to) {
      toast({
        description: "Please select Date range Show Date before searching.",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
      if (isExportExcel) setIsExportExcel(false);
      return;
    }

    try {
      const params = {
        show_id: showSearch || null,
        venue_id: venueSearch || null,
        status: statusSearch || null,
        channel_id: channelSearch || null,
        unit_id: unitSearch || null,
        payment_method: paymentMethodSearch || null,
        payment_status: paymentStatusSearch || null,
        show_time: showTimeSearch || null,
        zone_id: zoneSearch || null,
        cancel_noti_status: sendingStatusSearch || null,
        cancel_noti_message_status: receivingStatusSearch || null,
        lang: langSearch || null,
        search_value: searchValue || null,
        show_date_from: watchSearch("show_date_from")
          ? dayjs(watchSearch("show_date_from")).format("YYYY-MM-DD")
          : undefined,
        show_date_to: watchSearch("show_date_to")
          ? dayjs(watchSearch("show_date_to")).format("YYYY-MM-DD")
          : undefined,
      };

      if (isExportExcel) {
        setIsExportExcel(false);
        const response = await bookingActions.exportCancelledNotificationReport(params);
        const outputFilename = `cancelled_notification_report_${Date.now()}.xlsx`;
        const url = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
      } else {
        setIsFetching(true);
        setReportData([]);
        setSelectedBookingIds([]);
        setSelectedGuestEmails([]);
        const res = await bookingActions.getCancelledNotificationReport(params);
        setReportData(res.data.data || []);
        setReportTotal(res.data.meta?.total ?? 0);
        setHasSearched(true);
        setIsFilterDirty(false);
      }
    } catch (error) {
      toast({
        description: `${error}`,
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsFetching(false);
    }
  }

  const fetchTemplate = async (lang) => {
    const templateCode = lang === "vi" ? "CANCEL_SHOW_VI" : "CANCEL_SHOW_EN";
    try {
      setIsLoadingTemplate(true);
      const res = await notificationActions.getActiveTemplateDetail(templateCode);
      if (res.status === 200 && res.data.data) {
        setTemplateSubject(res.data.data.subject || "");
        setTemplateBody(res.data.data.body || "");
      }
    } catch (error) {
      console.error("Failed to fetch template", error);
      toast({
        description: "Failed to load email template.",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  const handleSendCancelEmail = async () => {
    try {
      setIsSendingEmail(true);
      await bookingActions.sendCancelEmail({
        ids: selectedBookingIds.map(Number),
        email_cc: emailCC,
        lang: emailLanguage,
      });
      toast({
        description: "Cancel email sent successfully.",
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
      onEmailModalClose();
      handleSubmitSearch(onSubmitSearch)();
    } catch (error) {
      toast({
        description: `${error}`,
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };


  return (
    <Card px="0px">
      <CardHeader px="22px" mb="12px">
        <Flex direction="column" display="block">
          <Text
            float="left"
            color={textColor}
            fontSize="lg"
            fontWeight="bold"
            mb="6px"
          >
            Show Cancelled Notification
          </Text>
        </Flex>
      </CardHeader>
      <CardBody>
        {!checkForbidden(136) ? (
          <Flex justify="center" align="center" height="200px" color="gray.500">
            You do not have permission to view this page.
          </Flex>
        ) : (
        <>
        <Card>
          <form
            id="cancelled-notification-search-form"
            onSubmit={handleSubmitSearch(onSubmitSearch)}
          >
            <Flex
              direction="row"
              alignItems="center"
              mb={3}
              gap={4}
              flexWrap={"wrap"}
            >
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Show Date From
                </FormLabel>
                <Controller
                  name="show_date_from"
                  control={controlSearch}
                  render={({ field }) => (
                    <DatePickerInput
                      onChange={(value) => {
                        if (value) {
                          handleCalendarOpen(value.getFullYear(), value.getMonth(), true);
                        }
                        field.onChange(value);
                      }}
                      mask="99/99/9999"
                      selected={watchSearch("show_date_from")}
                      highlightDates={highlightDate}
                      selectsStart
                      endDate={watchSearch("show_date_to")}
                      minDate={new Date("01/01/2020")}
                      onCalendarOpen={() => {
                        const fromDate = watchSearch("show_date_from");
                        if (fromDate) {
                          handleCalendarOpen(fromDate.getFullYear(), fromDate.getMonth(), true);
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Show Date To
                </FormLabel>
                <Controller
                  name="show_date_to"
                  control={controlSearch}
                  render={({ field }) => (
                    <DatePickerInput
                      onChange={(value) => {
                        if (value) {
                          handleCalendarOpen(value.getFullYear(), value.getMonth(), false);
                        }
                        field.onChange(value);
                      }}
                      onCalendarOpen={() => {
                        const toDate = watchSearch("show_date_to");
                        if (toDate) {
                          handleCalendarOpen(toDate.getFullYear(), toDate.getMonth(), false);
                        }
                      }}
                      selected={watchSearch("show_date_to")}
                      mask="99/99/9999"
                      selectsEnd
                      endDate={new Date("99/99/9999")}
                      minDate={watchSearch("show_date_from") || new Date("01/01/2020")}
                      highlightDates={highlightDate}
                    />
                  )}
                />
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Search
                </FormLabel>
                <Input
                  ms="4px"
                  type="text"
                  size="sm"
                  placeholder="Search..."
                  {...registerSearch("searchValue")}
                  width="200px"
                />
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Show
                </FormLabel>
                <Select
                  ms="4px"
                  type="text"
                  defaultValue=""
                  size="sm"
                  {...registerSearch("showSearch")}
                  width="100px"
                >
                  <option value="">All</option>
                  {listShow.map((item, index) => (
                    <option key={index} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Venue
                </FormLabel>
                <Select
                  ms="4px"
                  type="text"
                  size="sm"
                  {...registerSearch("venueSearch")}
                >
                  <option value="">All</option>
                  {venueList.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Show Time
                </FormLabel>
                <Select
                  ms="4px"
                  type="text"
                  defaultValue=""
                  size="sm"
                  {...registerSearch("showTimeSearch")}
                >
                  <option value="">All</option>
                  {showTimeList.map((item, index) => (
                    <option key={index} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Channel
                </FormLabel>
                <Select
                  ms="4px"
                  type="text"
                  size="sm"
                  {...registerSearch("channelSearch")}
                  width="200px"
                >
                  <option value="">All</option>
                  {listChannel.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Unit
                </FormLabel>
                <Select
                  ms="4px"
                  type="text"
                  size="sm"
                  {...registerSearch("unitSearch")}
                >
                  <option value="">All</option>
                  {listUnit.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Zone
                </FormLabel>
                <Select
                  ms="4px"
                  type="text"
                  size="sm"
                  {...registerSearch("zoneSearch")}
                  width="200px"
                >
                  <option value="">All</option>
                  {listZone.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Payment Status
                </FormLabel>
                <Select
                  ms="4px"
                  type="text"
                  defaultValue=""
                  size="sm"
                  {...registerSearch("paymentStatusSearch")}
                  width="100px"
                >
                  <option value="">All</option>
                  {listPaymentStatus.map((item, index) => (
                    <option key={index} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Payment Method
                </FormLabel>
                <Select
                  ms="4px"
                  type="text"
                  size="sm"
                  {...registerSearch("paymentMethodSearch")}
                >
                  <option value="">All</option>
                  {listPaymentMethod.map((item, index) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Booking Status
                </FormLabel>
                <Select
                  ms="4px"
                  type="text"
                  defaultValue=""
                  size="sm"
                  {...registerSearch("statusSearch")}
                  width="100px"
                >
                  {bookingStatus.map((item, index) => (
                    <option key={index} value={item.Id}>
                      {item.Name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Sending Status
                </FormLabel>
                <Select
                  ms="4px"
                  type="text"
                  defaultValue=""
                  size="sm"
                  {...registerSearch("sendingStatusSearch")}
                  width="120px"
                >
                  {sendingStatusOptions.map((item, index) => (
                    <option key={index} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Receiving Status
                </FormLabel>
                <Select
                  ms="4px"
                  type="text"
                  defaultValue=""
                  size="sm"
                  {...registerSearch("receivingStatusSearch")}
                  width="120px"
                >
                  {receivingStatusOptions.map((item, index) => (
                    <option key={index} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                  Language
                </FormLabel>
                <Select
                  ms="4px"
                  type="text"
                  defaultValue=""
                  size="sm"
                  {...registerSearch("langSearch")}
                  width="120px"
                >
                  {languageOptions.map((item, index) => (
                    <option key={index} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Box>
            </Flex>
            <Flex justify="center" align="center" gap={{ base: "4px", md: "8px" }} mt={3} flexWrap="wrap">
              <Box direction="row" alignItems="center" display="block">
                <Button
                  leftIcon={<RxReset />}
                  variant="primary"
                  w={{ base: "70px", md: "88px" }}
                  h="33px"
                  fontSize={{ base: "sm", md: "md" }}
                  onClick={() => {
                    resetSearch();
                    setValueSearch("show_date_from", new Date());
                    setValueSearch("show_date_to", new Date());
                  }}
                >
                  Reset
                </Button>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <Button
                  leftIcon={<RiFileExcel2Fill />}
                  variant="primary"
                  w={{ base: "80px", md: "100px" }}
                  h="33px"
                  fontSize={{ base: "sm", md: "md" }}
                  isDisabled={!hasSearched || isFilterDirty || reportTotal === 0}
                  onClick={() => {
                    setIsExportExcel(true);
                  }}
                  type="submit"
                >
                  <Text display={{ base: "none", md: "block" }}>Export Excel</Text>
                  <Text display={{ base: "block", md: "none" }}>Export</Text>
                </Button>
              </Box>
              <Box direction="row" alignItems="center" display="block">
                <Button
                  leftIcon={<BsSearch />}
                  variant="primary"
                  w={{ base: "70px", md: "88px" }}
                  h="33px"
                  fontSize={{ base: "sm", md: "md" }}
                  type="submit"
                >
                  Search
                </Button>
              </Box>
              {checkForbidden(137) && (
                <Box direction="row" alignItems="center" display="block" ml={{ base: "4px", md: "16px" }}>
                  <Button
                    leftIcon={<MdEmail />}
                    colorScheme="teal"
                    h="33px"
                    fontSize={{ base: "sm", md: "md" }}
                    minW={{ base: "auto", md: "auto" }}
                    px={{ base: "12px", md: "16px" }}
                    isDisabled={selectedBookingIds.length === 0}
                    onClick={() => {
                      setEmailLanguage("en");
                      setEmailCC("");
                      fetchTemplate("en");
                      onEmailModalOpen();
                    }}
                  >
                    <Text display={{ base: "none", md: "block" }}>Send Email ({selectedBookingIds.length})</Text>
                    <Text display={{ base: "block", md: "none" }}>Send ({selectedBookingIds.length})</Text>
                  </Button>
                </Box>
              )}
            </Flex>
          </form>
        </Card>
        {isFetching && (
          <Flex justify="center" align="center" my="20px">
            <Spinner
              textAlign="center"
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="blue.500"
              size="xl"
            />
          </Flex>
        )}
        <div
          ref={contentRef}
          className='content'
          style={{ display: !isFetching && reportData && reportData.length > 0 ? 'block' : 'none' }}
          dangerouslySetInnerHTML={{ __html: reportData }}
        ></div>
        {!isFetching && (!reportData || reportData.length === 0) && (
          <Flex
            justify="center"
            align="center"
            height="200px"
            color="gray.500"
          >
            No data available. Please click Search to load data.
          </Flex>
        )}
      {/* Send Cancel Email Modal */}
      <Modal isOpen={isEmailModalOpen} onClose={onEmailModalClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW="900px">
          <ModalHeader>Send Cancelled Notification Email</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel fontWeight="semibold" fontSize="sm">To (Guest Emails)</FormLabel>
              <Textarea
                value={selectedGuestEmails.join(", ")}
                isReadOnly
                size="sm"
                bg="gray.100"
                rows={Math.min(Math.ceil(selectedGuestEmails.length / 3), 5) || 1}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel fontWeight="semibold" fontSize="sm">CC</FormLabel>
              <Input
                value={emailCC}
                onChange={(e) => setEmailCC(e.target.value)}
                placeholder="Enter CC email addresses"
                size="sm"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel fontWeight="semibold" fontSize="sm">Language</FormLabel>
              <Select
                value={emailLanguage}
                onChange={(e) => {
                  setEmailLanguage(e.target.value);
                  fetchTemplate(e.target.value);
                }}
                size="sm"
                width="200px"
              >
                <option value="en">English</option>
                <option value="vi">Tiếng Việt</option>
              </Select>
            </FormControl>
            <FormControl mb={4}>
              <FormLabel fontWeight="semibold" fontSize="sm">Subject</FormLabel>
              {isLoadingTemplate ? (
                <Spinner size="sm" />
              ) : (
                <Text fontSize="sm" p={2} bg="gray.100" borderRadius="md">
                  {templateSubject || "No subject available"}
                </Text>
              )}
            </FormControl>
            <FormControl mb={4}>
              <FormLabel fontWeight="semibold" fontSize="sm">Body</FormLabel>
              {isLoadingTemplate ? (
                <Flex justify="center" align="center" height="200px">
                  <Spinner size="md" />
                </Flex>
              ) : (
                <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
                  <iframe
                    title="Email Preview"
                    srcDoc={templateBody}
                    style={{ width: "100%", height: "400px", border: "none" }}
                    sandbox=""
                  />
                </Box>
              )}
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEmailModalClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              isLoading={isSendingEmail}
              onClick={handleSendCancelEmail}
            >
              Send Email
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
        </>
        )}
    </CardBody>
    </Card>
  );
}

export default DataTableShowCancelledNotification;
